import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import HubCountryLinks from "@/components/HubCountryLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/police-clearance — Hub (EN)
import { createFileRoute, Link } from "@tanstack/react-router";
import { POLICE_CLEARANCE_TYPES } from "@/data/police-clearance-types";
import { TARGET_COUNTRIES } from "@/data/police-target-countries";
import { policeHubSectionsEn } from "@/data/police-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { ShieldCheck, Globe2, MessageCircle, Phone, MapPin } from "lucide-react";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("police", "en", "https://nyclanguageinstitute.com/en/police-clearance") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/en/police-clearance`;
    const title = "Police Clearance & Criminal Record Check — PCSC, FBI, ACRO, AFP, RCMP | NYC";
    const desc = "Comprehensive Police Clearance service — Thai PCSC, FBI, ACRO, AFP, RCMP. Send your documents for a free eligibility check.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance", "en") },
        { name: "description", content: ctaDesc(desc, "/en/police-clearance", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/police-clearance", "/en/police-clearance")],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: "Police Clearance & Criminal Record Check",
              serviceType: "Police Clearance Coordination",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 2000, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Police Clearance tooling (PCSC, FBI, ACRO, AFP, RCMP)." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: PoliceHubEnPage,
});

function PoliceHubEnPage() {
  const sections = policeHubSectionsEn();
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-5xl px-4 pt-6">
        <AIAnswerBox
          question="How does NYC handle Police Clearance for any country?"
          answer={`NYC processes ${POLICE_CLEARANCE_TYPES.length} document types (Thai PCSC, FBI, ACRO, AFP, RCMP and more) for ${TARGET_COUNTRIES.length}+ destination countries — translation + MFA + Apostille bundled in one package. From THB 2,000, by Lawyers Council-registered attorneys.`}
          stats={[
            { label: "Doc types", value: `${POLICE_CLEARANCE_TYPES.length}` },
            { label: "Destinations", value: `${TARGET_COUNTRIES.length}+` },
            { label: "From", value: "THB 2,000" },
            { label: "Attorneys", value: "Lawyers Council" },
          ]}
          sourceUrl={`${SITE}/en/police-clearance`}
          lang="en"
        />
      </div>
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Police Clearance</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Police Clearance · Criminal Record Check
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Police Clearance for Every Country — Thai PCSC, FBI, ACRO, AFP, RCMP
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            End-to-end service — 8 document types · 60+ destination countries · translation + MFA/Apostille in one package. From THB 2,000, by Lawyers Council-registered attorneys.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <PoliceSections sections={sections} />

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Choose Your Service Type</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {POLICE_CLEARANCE_TYPES.map((t) => (
              <Link
                key={t.slug}
                to="/en/police-clearance/$type"
                params={{ type: t.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50 hover:shadow-md transition"
              >
                <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {t.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">From THB {t.priceFromTh.toLocaleString()} · {t.turnaroundEn}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            For Destination Country (60+)
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {TARGET_COUNTRIES.map((c) => (
              <Link
                key={c.slug}
                to="/en/police-clearance/for/$country"
                params={{ country: c.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50"
              >
                <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                <span className="font-semibold">{c.nameEn}</span>
                <span className="ml-auto text-[10px] text-slate-500">{c.hague ? "Apostille" : "Embassy"}</span>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-14"><ContactCTA /></div>
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="en" variant="light" />
          <HubCountryLinks service="police-clearance" lang="en" />
          <ServicePageEnhancer serviceName="Police Clearance" locale="en" />
<RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
